import axios from 'axios';

const getAuthToken = (): string | null => {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'session_token') {
      return value;
    }
  }
  return null;
};

const BOOKINGS_API_URL = import.meta.env.VITE_BFF_API_URL || 'http://localhost:8086';
const AUTH_APP_URL = import.meta.env.VITE_AUTH_APP_URL || 'http://localhost:3000';
const bookingsClient = axios.create({
  baseURL: BOOKINGS_API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

bookingsClient.interceptors.request.use(config => {
  const token = getAuthToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

bookingsClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401 && typeof window !== 'undefined') {
      window.location.href = AUTH_APP_URL;
    }
    return Promise.reject(error);
  },
);

// --- Types ---
export type BookingStatus = 'confirmed' | 'checked_in' | 'checked_out' | 'cancelled' | 'no_show';
export type BookingSource = 'direct' | 'walk_in' | 'online' | 'agent' | 'phone';
export type PaymentMethod = 'mpesa' | 'cash' | 'card' | 'room_charge';
export type PaymentStatus = 'pending' | 'completed' | 'failed' | 'refunded';

export interface RoomInfo {
  id: string;
  room_number: string;
  floor: number;
  wing?: string;
  room_type_id: string;
  room_type?: string;
  base_rate: number;
  description?: string;
}

export interface Guest {
  id: string;
  first_name: string;
  last_name: string;
  middle_name?: string;
  email?: string;
  phone: string;
  alternative_phone?: string;
  id_type: 'national_id' | 'passport' | 'drivers_license' | 'military_id' | 'other';
  id_number?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  nationality?: string;
  date_of_birth?: string;
  gender?: string;
  is_vip: boolean;
  vip_notes?: string;
  vip_level: number;
  is_blacklisted: boolean;
  blacklist_reason?: string;
  blacklisted_at?: string;
  preferences?: Record<string, unknown>;
  total_stays: number;
  total_spent: number;
  last_stay_at?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relationship?: string;
  created_at: string;
  updated_at: string;
  paymentSummary?: {
    meal_plan_name: string;
    meal_plan_price: number;
    nights: number;
    rate_per_night: number;
    room_charge: number;
    total_amount: number;
  };
}

export interface BookingNight {
  id: string;
  booking_id: string;
  date: string;
  room_id?: string;
  room_type_id?: string;
  price_applied: number;
  rate_source: string;
  meal_plan_id?: string;
  meal_plan_price?: number;
}

export interface Booking {
  id: string;
  booking_reference: string;
  guest_id: string;
  room_id: string;
  room_type_id?: string;
  guest?: Guest;
  room?: RoomInfo;
  check_in_date: string;
  check_out_date: string;
  actual_check_in?: string;
  actual_check_out?: string;
  adults: number;
  children: number;
  meal_plan_id?: string;
  meal_plan_price?: number;
  total_amount: number;
  amount_paid: number;
  balance: number;
  payment_status?: 'pending' | 'deposit' | 'paid';
  deposit_amount?: number;
  is_complimentary?: boolean;
  status: BookingStatus;
  booking_source: BookingSource;
  rate_per_night: number;
  number_of_nights: number;
  negotiated_rate?: number | null;
  is_negotiated_rate?: boolean;
  metadata?: {
    payment?: {
      transaction_id?: string;
      method?: string;
      amount?: number;
      timestamp?: string;
    };
  };
  special_requests?: string;
  dietary_requirements?: string;
  cancellation_reason?: string;
  cancelled_at?: string;
  cancellation_fee?: number;
  additional_guests?: {
    first_name: string;
    last_name: string;
    id_type?: string;
    id_number?: string;
    relation?: string;
  }[];
  linked_account_type?: string;
  linked_account_name?: string;
  linked_account_reference?: string;
  created_at: string;
  updated_at: string;
}

export interface BookingPayment {
  id: string;
  booking_id: string;
  amount: number;
  payment_method: PaymentMethod;
  status: PaymentStatus;
  reference?: string;
  mpesa_receipt?: string;
  payment_date: string;
  notes?: string;
}

export interface PaginatedGuests {
  guests: Guest[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface PaginatedBookings {
  bookings: Booking[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface CreateGuestInput {
  first_name: string;
  last_name: string;
  middle_name?: string;
  email?: string;
  phone: string;
  alternative_phone?: string;
  id_type?: 'national_id' | 'passport' | 'drivers_license' | 'military_id' | 'other';
  id_number?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  nationality?: string;
  date_of_birth?: string;
  gender?: string;
  is_vip?: boolean;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relationship?: string;
}

export interface BookingNightInput {
  date: string;
  room_id?: string;
  room_type_id?: string;
  price: number;
  rate_source: string;
  meal_plan_id?: string;
  meal_plan_price?: number;
}

export interface CreateBookingInput {
  guest_id: string;
  room_id?: string;
  room_type_id?: string;
  check_in_date: string;
  check_out_date: string;
  adults?: number;
  children?: number;
  meal_plan_id?: string;
  meal_plan_price?: number;
  booking_source?: BookingSource;
  payment_status?: 'pending' | 'deposit' | 'paid';
  deposit_amount?: number;
  is_complimentary?: boolean;
  payment_method?: PaymentMethod;
  special_requests?: string;
  is_negotiated_rate?: boolean;
  negotiated_rate?: number;
  nights?: BookingNightInput[];
  additional_guests?: {
    first_name: string;
    last_name: string;
    id_type?: string;
    id_number?: string;
    relation?: string;
  }[];
  linked_account_type?: string;
  linked_account_name?: string;
  linked_account_reference?: string;
}

export interface UpdateBookingInput {
  guest_id?: string;
  room_id?: string;
  room_type_id?: string;
  check_in_date?: string;
  check_out_date?: string;
  adults?: number;
  children?: number;
  meal_plan_id?: string;
  meal_plan_price?: number;
  booking_source?: BookingSource;
  total_amount?: number;
  payment_status?: 'pending' | 'deposit' | 'paid';
  deposit_amount?: number;
  is_complimentary?: boolean;
  payment_method?: PaymentMethod;
  status?: BookingStatus;
  is_negotiated_rate?: boolean;
  negotiated_rate?: number;
  special_requests?: string;
  nights?: BookingNightInput[];
  additional_guests?: {
    first_name: string;
    last_name: string;
    id_type?: string;
    id_number?: string;
    relation?: string;
  }[];
  linked_account_type?: string;
  linked_account_name?: string;
  linked_account_reference?: string;
}

// --- Guest Service ---
export interface GuestSearchParams {
  q?: string;
  page?: number;
  page_size?: number;
}

export const guestService = {
  search: async (params?: GuestSearchParams): Promise<PaginatedGuests> => {
    const res = await bookingsClient.get<PaginatedGuests>('/api/v1/guests/search', { params });
    return res.data;
  },
  get: (id: string) => bookingsClient.get<Guest>(`/api/v1/guests/${id}`),
  create: (data: CreateGuestInput) => bookingsClient.post<Guest>('/api/v1/guests', data),
  update: (id: string, data: Partial<Guest>) =>
    bookingsClient.put<Guest>(`/api/v1/guests/${id}`, data),
  delete: (id: string) => bookingsClient.delete(`/api/v1/guests/${id}`),
  blacklist: (id: string, reason: string) =>
    bookingsClient.post<Guest>(`/api/v1/guests/${id}/blacklist`, { reason }),
  unblacklist: (id: string) => bookingsClient.delete<Guest>(`/api/v1/guests/${id}/blacklist`),
};

// --- Booking Service ---
export interface BookingListParams {
  page?: number;
  page_size?: number;
}

export const bookingService = {
  listActive: async (params?: BookingListParams): Promise<PaginatedBookings> => {
    const res = await bookingsClient.get<PaginatedBookings>('/api/v1/bookings/active', { params });
    return res.data;
  },
  listUpcoming: async (params?: BookingListParams): Promise<PaginatedBookings> => {
    const res = await bookingsClient.get<PaginatedBookings>('/api/v1/bookings/upcoming', {
      params,
    });
    return res.data;
  },
  listAll: async (params?: BookingListParams): Promise<PaginatedBookings> => {
    const res = await bookingsClient.get<PaginatedBookings>('/api/v1/bookings/all', { params });
    return res.data;
  },
  listDeparturesToday: async (params?: BookingListParams): Promise<PaginatedBookings> => {
    const res = await bookingsClient.get<PaginatedBookings>('/api/v1/bookings/departures/today', {
      params,
    });
    return res.data;
  },
  get: (id: string) => bookingsClient.get<Booking>(`/api/v1/bookings/${id}`),
  create: (data: CreateBookingInput) => bookingsClient.post<Booking>('/api/v1/bookings', data),
  update: (id: string, data: Partial<CreateBookingInput>) =>
    bookingsClient.put<Booking>(`/api/v1/bookings/${id}`, data),
  checkIn: (id: string, data?: { room_id?: string }) =>
    bookingsClient.post<Booking>(`/api/v1/bookings/${id}/checkin`, data),
  checkOut: (id: string) => bookingsClient.post<Booking>(`/api/v1/bookings/${id}/checkout`),
  cancel: (id: string, reason?: string) =>
    bookingsClient.post<Booking>(`/api/v1/bookings/${id}/cancel`, { reason }),
  markNoShow: (id: string) => bookingsClient.post<Booking>(`/api/v1/bookings/${id}/no-show`),
  releaseRoom: (id: string) => bookingsClient.post<Booking>(`/api/v1/bookings/${id}/release-room`),
  transfer: (id: string, data: { room_id: string; note: string }) =>
    bookingsClient.post<Booking>(`/api/v1/bookings/${id}/transfer`, data),
  processPayment: (id: string, data: { amount: number; method: string; phone: string }) =>
    bookingsClient.post<Booking>(`/api/v1/bookings/${id}/payment`, data),
  markAsPaid: (
    id: string,
    data: { amount: number; method: string; transactionId: string; tipAmount?: number },
  ) =>
    bookingsClient.post<Booking>(`/api/v1/bookings/${id}/payment`, {
      amount: data.amount,
      method: data.method,
      transaction_id: data.transactionId,
      tip_amount: data.tipAmount || 0,
    }),
  listPayments: (id: string) =>
    bookingsClient
      .get<{ payments: BookingPayment[] }>(`/api/v1/bookings/${id}/payments`)
      .then(r => r.data),
};

// --- Export aggregate ---
export const bookingsApi = {
  guestService,
  bookingService,
};

export default bookingsApi;
